import type { CCCCardView } from "../../shared/types";

/** "The Gambler" doesn't have a fixed bonus - its actual per-turn bonus is only
 * decided once drawing starts (see GameStateMachine.getActiveArtistCCCViews),
 * so its own undetermined card (familyId still "theGambler") shows "???"
 * rather than a number. Once resolved, the broadcast view carries the real
 * assigned family/level and a real randomized bonusPercent, so this only ever
 * applies to the base, not-yet-decided card itself. */
export function formatBonusPercent(card: CCCCardView): string {
  return card.familyId === "theGambler" ? "???" : String(card.bonusPercent);
}

/** Wraps `label` (defaults to the card's name) in a hoverable span that reveals a
 * small card-styled tooltip with the family's name/level/effect/bonus - used
 * anywhere a CCC name is shown in passing (the "Active: ..." canvas indicator, the
 * word-selection screen's active-card list) without repeating the full card. */
export function buildCCCNameHover(card: CCCCardView, label: string = card.name): HTMLElement {
  const wrap = document.createElement("span");
  wrap.className = "ccc-name-hover";
  wrap.textContent = label;
  wrap.tabIndex = 0;

  const tooltip = document.createElement("div");
  tooltip.className = "ccc-tooltip";

  const name = document.createElement("div");
  name.className = "ccc-tooltip-name";
  name.textContent = card.name;

  const level = document.createElement("div");
  level.className = "ccc-tooltip-level";
  level.textContent = `Level ${card.level}`;

  const desc = document.createElement("div");
  desc.className = "ccc-tooltip-desc";
  desc.textContent = card.description;

  const bonus = document.createElement("div");
  bonus.className = "ccc-tooltip-bonus";
  bonus.textContent = `+${formatBonusPercent(card)}% drawing bonus points`;

  tooltip.append(name, level, desc, bonus);
  wrap.appendChild(tooltip);

  // Positioned via JS (fixed to the viewport) rather than pure CSS so it can
  // flip above/below and clamp horizontally based on actual available space -
  // a purely CSS-positioned tooltip (absolute, anchored below the name) still
  // counts toward its scrolling ancestor's scrollHeight wherever it lands, so
  // a long description hovered near the bottom of a scroll container (e.g.
  // the word-selection screen's active-card list) could grow a scrollbar for
  // content that's only ever a transient hover preview.
  function showTooltip(): void {
    tooltip.style.display = "block";
    const wrapRect = wrap.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const margin = 8;

    const fitsBelow = wrapRect.bottom + margin + tooltipRect.height <= window.innerHeight;
    const top = fitsBelow ? wrapRect.bottom + margin : wrapRect.top - margin - tooltipRect.height;

    const idealLeft = wrapRect.left + wrapRect.width / 2 - tooltipRect.width / 2;
    const left = Math.max(margin, Math.min(idealLeft, window.innerWidth - tooltipRect.width - margin));

    tooltip.style.top = `${Math.max(margin, top)}px`;
    tooltip.style.left = `${left}px`;
  }

  function hideTooltip(): void {
    tooltip.style.display = "none";
  }

  wrap.addEventListener("mouseenter", showTooltip);
  wrap.addEventListener("mouseleave", hideTooltip);
  wrap.addEventListener("focus", showTooltip);
  wrap.addEventListener("blur", hideTooltip);

  return wrap;
}
